import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';

// Landing page for the staff help section. Topics are rows in help_topics, so
// adding one is a content change made in the app itself -- no deploy.
const Help = () => {
  const navigate = useNavigate();
  const [topics, setTopics]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('help_topics')
      .select('slug, title, blurb, category, sort_order, active')
      .eq('active', true)
      .order('category')
      .order('sort_order');
    setTopics(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Grouped in render order rather than sorted here -- the query already
  // returns category/sort_order order, so insertion order into the map is
  // the display order.
  const byCategory = useMemo(() => {
    const groups = new Map();
    for (const t of topics) {
      const key = t.category || 'General';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(t);
    }
    return [...groups.entries()];
  }, [topics]);

  const createTopic = async () => {
    setCreating(true);
    // Slug is unique, so a fixed "new-topic" would collide on the second
    // create. Timestamp suffix keeps it collision-free until renamed.
    const slug = `new-topic-${Date.now().toString(36)}`;
    const { error } = await supabase.from('help_topics').insert({
      slug,
      title:    'New topic',
      category: 'General',
      blurb:    '',
    });
    setCreating(false);
    if (error) { alert(`Could not create topic: ${error.message}`); return; }
    navigate(`/help/${slug}?edit=1`);
  };

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/utilities')}>← Utilities</button>
        <span className="page-nav__title">Help &amp; Process Guides</span>
      </div>

      <section className="section">
        <SectionHeader
          title="How Things Work"
          count={topics.length}
          onRefresh={load}
          loading={loading}
          actions={
            <button className="action-btn action-btn--ghost" onClick={createTopic} disabled={creating}>
              {creating ? 'Creating…' : '+ New Topic'}
            </button>
          }
        />
        <p className="section__desc">
          Guides to the processes running behind the portal — what the system does on its own,
          and where your part starts.
        </p>

        {loading && <div className="loading-row">Loading…</div>}
        {!loading && !topics.length && (
          <div className="empty-row">No help topics yet — use “+ New Topic” to add the first one.</div>
        )}

        {byCategory.map(([category, list]) => (
          <div key={category} className="help-group">
            <div className="help-group__title">{category}</div>
            <div className="help-grid">
              {list.map((t) => (
                <button
                  key={t.slug}
                  className="help-card"
                  onClick={() => navigate(`/help/${t.slug}`)}
                >
                  <div className="help-card__title">{t.title}</div>
                  {t.blurb && <div className="help-card__blurb">{t.blurb}</div>}
                  <div className="help-card__arrow">Read →</div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </section>
    </main>
  );
};

export default Help;
